import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarClock, MapPin, User as UserIcon, Wrench, FileText, Receipt, Clock, AlertTriangle } from "lucide-react";

const STAGE_META = {
  scheduling: { label: "Scheduling",         color: "bg-amber-100 text-amber-800 border-amber-200",   icon: CalendarClock },
  execution:  { label: "Job execution",      color: "bg-blue-100 text-blue-800 border-blue-200",      icon: Wrench },
  analysis:   { label: "Analysis & report",  color: "bg-violet-100 text-violet-800 border-violet-200", icon: FileText },
  invoice:    { label: "Invoice submission", color: "bg-orange-100 text-orange-800 border-orange-200", icon: Receipt },
  completed:  { label: "Completed",          color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Clock },
};

const REPORT_BADGE = {
  not_started:    { l: "Report — not started", c: "bg-slate-100 text-slate-600" },
  in_progress:    { l: "Report — in progress", c: "bg-blue-100 text-blue-800" },
  awaiting_review:{ l: "Report — awaiting review", c: "bg-amber-100 text-amber-800" },
  submitted:      { l: "Report — submitted", c: "bg-emerald-100 text-emerald-800" },
};

const INVOICE_BADGE = {
  not_started: { l: "Invoice — pending", c: "bg-slate-100 text-slate-600" },
  drafted:     { l: "Invoice — drafted", c: "bg-blue-100 text-blue-800" },
  submitted:   { l: "Invoice — submitted", c: "bg-amber-100 text-amber-800" },
  paid:        { l: "Invoice — paid", c: "bg-emerald-100 text-emerald-800" },
};

function ServiceJobCard({ job }) {
  const sm = STAGE_META[job.stage] || STAGE_META.scheduling;
  const StageIcon = sm.icon;
  const isDelayed = (() => {
    if (job.stage === "completed") return false;
    const t = job.tentative_schedule_end || job.tentative_schedule_start;
    if (!t) return false;
    return new Date(t) < new Date();
  })();
  return (
    <Link to={`/service-execution/${job.id}`} data-testid={`service-job-card-${job.id}`}>
      <Card className="rounded-3xl border-border/60 hover:shadow-soft transition card-lift">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg font-extrabold tracking-tight truncate" data-testid={`service-job-title-${job.id}`}>
                {job.deal_title || "Untitled service job"}
              </p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {job.client_name || "—"} · {fmtINR(job.deal_value)}
              </p>
            </div>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${sm.color}`}>
              <StageIcon className="h-3 w-3 mr-1" /> {sm.label}
            </Badge>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <UserIcon className="h-3.5 w-3.5 shrink-0" />
              <span>Engineer: <span className="font-semibold text-foreground">{job.engineer_name || (job.self_execute ? "Self" : "Not assigned")}</span></span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5 shrink-0" />
              <span>
                {job.tentative_schedule_start ? `${fmtDate(job.tentative_schedule_start)}${job.tentative_schedule_end ? " → " + fmtDate(job.tentative_schedule_end) : ""}` : "No schedule yet"}
                {job.service_duration_days ? ` · ${job.service_duration_days}d` : ""}
              </span>
            </div>
            {job.site_location && (
              <div className="flex items-center gap-2 text-muted-foreground sm:col-span-2">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{job.site_location}</span>
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            <Badge variant="outline" className={`text-[10px] ${REPORT_BADGE[job.report_status || "not_started"].c}`}>
              {REPORT_BADGE[job.report_status || "not_started"].l}
            </Badge>
            <Badge variant="outline" className={`text-[10px] ${INVOICE_BADGE[job.invoice_status || "not_started"].c}`}>
              {INVOICE_BADGE[job.invoice_status || "not_started"].l}
            </Badge>
            {isDelayed && (
              <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200" data-testid={`service-job-delayed-${job.id}`}>
                <AlertTriangle className="h-3 w-3 mr-1" /> Delayed
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function ServiceExecution() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/service-jobs", { params: { stage: stage || undefined } });
        if (!cancelled) setRows(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stage]);

  const counts = rows.reduce((acc, j) => { acc[j.stage] = (acc[j.stage] || 0) + 1; return acc; }, {});

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-7xl">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Operations</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Service Execution.</h1>
          <p className="text-sm text-muted-foreground mt-2">Operational pipeline for Won technical-services deals — scheduling through invoicing.</p>
        </div>
      </header>

      {/* Stage chips — quick filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="service-execution-stage-chips">
        <button
          type="button"
          onClick={() => setStage("")}
          data-testid="service-stage-chip-all"
          className={`px-4 h-9 rounded-2xl text-xs font-semibold border transition tap ${stage === "" ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted border-border/60"}`}
        >
          All <span className="opacity-70">· {rows.length}</span>
        </button>
        {Object.entries(STAGE_META).map(([k, m]) => (
          <button
            key={k}
            type="button"
            onClick={() => setStage(k)}
            data-testid={`service-stage-chip-${k}`}
            className={`px-4 h-9 rounded-2xl text-xs font-semibold border transition tap ${stage === k ? `${m.color}` : "bg-card hover:bg-muted border-border/60"}`}
          >
            {m.label} <span className="opacity-70">· {counts[k] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-3xl" />)}
        </div>
      ) : rows.length === 0 ? (
        <Card className="rounded-3xl border-dashed">
          <CardContent className="p-10 text-center">
            <Wrench className="h-10 w-10 mx-auto text-muted-foreground/60" />
            <p className="font-semibold mt-3">No service jobs yet</p>
            <p className="text-sm text-muted-foreground mt-1">When a Technical Services deal is marked <strong>Won</strong>, a job appears here automatically.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((j) => <ServiceJobCard key={j.id} job={j} />)}
        </div>
      )}
    </div>
  );
}
